/**
 * DashboardPage.tsx — Amargi v1.25.0
 * Dashboard de suivi longitudinal du système nerveux autonome.
 * Route : /fr/tableau-de-bord
 *
 * Composants :
 *   1. StreakBadge     — streak actif avec animation amber
 *   2. WeeklySummary   — score moyen 7j, profil dominant, nb sessions
 *   3. ScoreChart      — courbe SVG des 30 dernières sessions
 *   4. Heatmap8j       — 8 cases J1-J8 (done/today/future)
 *   5. ProfileHistory  — timeline des codes profil successifs
 *
 * Design system Amargi v2.0 : tokens lapis/amber/ink/paper/sand/taupe.
 * Layout max-w-lg centré, fond paper, 0 couleur hardcodée.
 * Pas de librairie graphique externe — SVG/CSS pur uniquement.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { m } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Flame, TrendingUp, Calendar, BarChart2, Clock, RefreshCw, Sparkles } from 'lucide-react';
import SiteNav from './SiteNav';
import BlogFooter from './BlogFooter';
import AppLink from './AppLink';
import SEO from './SEO';
import {
  getExistingUserId,
  getAggregates,
  getRecentSessions,
  AggregateData,
  SessionData,
} from '../lib/userSession';
import { PROFILES } from '../data/profiles';
import { useAuthContext } from '../contexts/AuthContext';
import { redirectToCheckout, type MolliePlanId } from '../lib/paymentService';

// ─── Types locaux ─────────────────────────────────────────────────────────────

interface SessionWithMeta extends SessionData {
  id: string;
  completedAt: string; // ISO 8601
  date: string;
}

// ─── Utilitaires ─────────────────────────────────────────────────────────────

function formatDate(completedAt: string): string {
  return completedAt.slice(0, 10);
}

function shortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function getZoneColor(code: string): string {
  const profile = PROFILES[code];
  if (!profile) return 'text-taupe';
  switch (profile.zone) {
    case 'ventral':    return 'text-ventral';
    case 'sympathique': return 'text-amber';
    case 'dorsal':     return 'text-lapis';
    case 'crise':      return 'text-crise';
    default:           return 'text-taupe';
  }
}

function getZoneBg(code: string): string {
  const profile = PROFILES[code];
  if (!profile) return 'bg-sand/30';
  switch (profile.zone) {
    case 'ventral':    return 'bg-ventral-bg';
    case 'sympathique': return 'bg-amber/8';
    case 'dorsal':     return 'bg-lapis/8';
    case 'crise':      return 'bg-crise-bg';
    default:           return 'bg-sand/30';
  }
}

function getZoneBorder(code: string): string {
  const profile = PROFILES[code];
  if (!profile) return 'border-sand';
  switch (profile.zone) {
    case 'ventral':    return 'border-ventral-border';
    case 'sympathique': return 'border-amber/25';
    case 'dorsal':     return 'border-lapis/25';
    case 'crise':      return 'border-crise-border';
    default:           return 'border-sand';
  }
}

// ─── Plan helpers ────────────────────────────────────────────────────────────

const PLAN_EXPIRY_DAYS: Record<string, number> = {
  ancrage:     56,
  integration: 84,
  praticien:   180,
};

const PLAN_DISPLAY: Record<string, string> = {
  ancrage:     'Ancrage',
  integration: 'Intégration',
  praticien:   'Praticien',
};

function daysRemaining(fromIso: string, plusDays: number): number {
  const target = new Date(fromIso);
  target.setDate(target.getDate() + plusDays);
  return Math.max(0, Math.ceil((target.getTime() - Date.now()) / 86_400_000));
}

function daysElapsed(fromIso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(fromIso).getTime()) / 86_400_000));
}

function formatShortDate(isoDate: string, plusDays: number): string {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + plusDays);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ─── Plan banner (free / trial_8j) ───────────────────────────────────────────

const FreeBanner = ({ onCheckout }: { onCheckout: () => void }) => (
  <div className="bg-lapis/8 border border-lapis/20 rounded-2xl p-5 mb-5">
    <p className="text-[14px] font-semibold text-ink mb-1">
      Vous êtes en accès gratuit.
    </p>
    <p className="text-[12px] text-taupe leading-relaxed mb-4">
      Découvrez l'accès complet pour suivre votre progression en profondeur et accéder aux protocoles avancés.
    </p>
    <button
      onClick={onCheckout}
      className="inline-flex items-center gap-2 bg-lapis text-white rounded-xl px-4 py-2.5 text-[13px] font-medium hover:bg-lapis-deep transition-all"
    >
      Commencer Ancrage · 19 €
      <ArrowRight className="w-3.5 h-3.5" />
    </button>
  </div>
);

const TrialBanner = ({ createdAt, onCheckout }: { createdAt: string; onCheckout: () => void }) => {
  const remaining = daysRemaining(createdAt, 8);
  return (
    <div className="bg-amber/8 border border-amber/25 rounded-2xl p-5 mb-5">
      <p className="text-[14px] font-semibold text-ink mb-1">
        {remaining > 0
          ? `Il vous reste ${remaining} jour${remaining > 1 ? 's' : ''} d'essai.`
          : "Votre période d'essai est terminée."}
      </p>
      <p className="text-[12px] text-taupe leading-relaxed mb-4">
        Continuez votre suivi sans interruption.
      </p>
      <button
        onClick={onCheckout}
        className="inline-flex items-center gap-2 bg-amber text-white rounded-xl px-4 py-2.5 text-[13px] font-medium hover:opacity-90 transition-all"
      >
        Continuer avec Ancrage · 19 €
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ─── Plan badge (pro / beta) ──────────────────────────────────────────────────

const ProBadge = ({ planActivatedAt, planKey }: { planActivatedAt: string; planKey: string }) => {
  const days = PLAN_EXPIRY_DAYS[planKey] ?? PLAN_EXPIRY_DAYS.ancrage;
  const name = PLAN_DISPLAY[planKey] ?? 'Pro';
  const expiry = formatShortDate(planActivatedAt, days);
  return (
    <div className="inline-flex items-center gap-2 bg-lapis/8 border border-lapis/20 rounded-full px-3 py-1 mb-4">
      <span className="w-1.5 h-1.5 rounded-full bg-lapis flex-shrink-0" />
      <span className="text-[11px] text-lapis font-medium">
        {name} · expire le {expiry}
      </span>
    </div>
  );
};

const BetaBadge = ({
  planActivatedAt,
  cohortName,
  onCheckout,
}: {
  planActivatedAt: string | null;
  cohortName: string | null;
  onCheckout: () => void;
}) => {
  const elapsed = planActivatedAt ? daysElapsed(planActivatedAt) : 0;
  const remaining30 = Math.max(0, 30 - elapsed);
  return (
    <div className="flex flex-col gap-2 mb-4">
      <div className="inline-flex items-center gap-2 bg-amber/10 border border-amber/25 rounded-full px-3 py-1 self-start">
        <Sparkles className="w-3 h-3 text-amber flex-shrink-0" />
        <span className="text-[11px] text-amber font-semibold">
          Fondatrice{cohortName ? ` · Cohorte ${cohortName}` : ''}
          {planActivatedAt ? ` · Jour ${elapsed}/30` : ''}
        </span>
      </div>
      {remaining30 > 0 && (
        <p className="text-[11px] text-taupe">
          {remaining30} jour{remaining30 > 1 ? 's' : ''} restant{remaining30 > 1 ? 's' : ''} dans votre période fondatrice.
        </p>
      )}
      <button
        onClick={onCheckout}
        className="self-start inline-flex items-center gap-1.5 text-[12px] text-taupe border border-sand rounded-xl px-3 py-1.5 hover:border-amber hover:text-amber transition-all mt-1"
      >
        Rejoindre à vie · 34,50 €
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
};

// ─── 1. StreakBadge ───────────────────────────────────────────────────────────

const StreakBadge = ({ streak, longest }: { streak: number; longest: number }) => {
  if (streak === 0) return null;
  return (
    <m.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      className="bg-white border border-sand rounded-2xl p-5 flex items-center gap-4 shadow-sm"
    >
      {/* Icône animée */}
      <div className="relative flex-shrink-0">
        <m.div
          animate={{ scale: [1, 1.12, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          className="w-12 h-12 rounded-xl bg-amber/12 flex items-center justify-center"
        >
          <Flame className="w-6 h-6 text-amber" />
        </m.div>
        {streak >= 3 && (
          <m.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.8, repeat: Infinity }}
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber flex items-center justify-center"
          >
            <span className="text-[8px] text-white font-bold">{streak}</span>
          </m.div>
        )}
      </div>
      {/* Texte */}
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold text-ink leading-tight">
          {streak === 1 ? '1 jour consécutif' : `${streak} jours consécutifs`}
        </p>
        <p className="text-[12px] text-taupe mt-0.5">
          {streak >= 7
            ? 'Série impressionnante — votre système nerveux s\'en souvient.'
            : streak >= 3
            ? 'Belle régularité — continuez sur cette lancée.'
            : 'Chaque jour compte. Revenez demain.'}
        </p>
      </div>
      {/* Record */}
      {longest > streak && (
        <div className="flex-shrink-0 text-right">
          <p className="text-[10px] text-taupe uppercase tracking-wide">Record</p>
          <p className="text-[14px] font-semibold text-taupe">{longest}j</p>
        </div>
      )}
    </m.div>
  );
};

// ─── 2. WeeklySummary ─────────────────────────────────────────────────────────

const WeeklySummary = ({
  avgScore7d,
  avgScore30d,
  dominantProfile,
  totalSessions,
}: {
  avgScore7d: number;
  avgScore30d: number;
  dominantProfile: string;
  totalSessions: number;
}) => {
  const profileName = PROFILES[dominantProfile]?.name?.split(' / ')[0]?.replace(/^L'|^Le |^La /, '') ?? dominantProfile;
  const delta = avgScore7d - avgScore30d;

  return (
    <div className="bg-white border border-sand rounded-2xl overflow-hidden shadow-sm">
      {/* En-tête */}
      <div className="px-5 pt-5 pb-3 border-b border-sand/60">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-lapis" />
          <p className="text-[11px] font-semibold text-lapis uppercase tracking-[1.5px]">Résumé 7 jours</p>
        </div>
      </div>
      {/* Grille 3 colonnes */}
      <div className="grid grid-cols-3 divide-x divide-sand/60">
        {/* Score moyen */}
        <div className="px-4 py-4 text-center">
          <p className="text-[26px] font-serif font-light text-ink leading-none">{avgScore7d}</p>
          <p className="text-[10px] text-taupe mt-1 uppercase tracking-wide">Score /100</p>
          {delta !== 0 && (
            <p className={`text-[11px] font-medium mt-1 ${delta > 0 ? 'text-ventral' : 'text-amber'}`}>
              {delta > 0 ? `+${delta}` : delta} vs 30j
            </p>
          )}
        </div>
        {/* Profil dominant */}
        <div className="px-4 py-4 text-center">
          <p className={`text-[13px] font-semibold leading-tight ${getZoneColor(dominantProfile)}`}>
            {dominantProfile}
          </p>
          <p className="text-[10px] text-taupe mt-1 uppercase tracking-wide">Profil</p>
          <p className="text-[10px] text-taupe mt-0.5 leading-tight line-clamp-2">{profileName}</p>
        </div>
        {/* Nb sessions */}
        <div className="px-4 py-4 text-center">
          <p className="text-[26px] font-serif font-light text-ink leading-none">{totalSessions}</p>
          <p className="text-[10px] text-taupe mt-1 uppercase tracking-wide">Sessions</p>
          <p className="text-[10px] text-taupe mt-0.5">au total</p>
        </div>
      </div>
    </div>
  );
};

// ─── 3. ScoreChart — courbe SVG des 30 dernières sessions ────────────────────

const ScoreChart = ({ sessions }: { sessions: SessionWithMeta[] }) => {
  if (sessions.length < 2) {
    return (
      <div className="bg-white border border-sand rounded-2xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <BarChart2 className="w-4 h-4 text-lapis" />
          <p className="text-[11px] font-semibold text-lapis uppercase tracking-[1.5px]">Évolution du score</p>
        </div>
        <p className="text-[13px] text-taupe text-center py-6">
          {sessions.length === 0
            ? 'Aucune session enregistrée.'
            : "Complétez une 2ème session pour voir votre courbe d'évolution."}
        </p>
      </div>
    );
  }

  // Préparer les données (chronologiques)
  const sorted = [...sessions].reverse();
  const W = 320;
  const H = 120;
  const PAD = { top: 12, right: 16, bottom: 28, left: 32 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const scores = sorted.map(s => s.score);
  const minScore = Math.max(0, Math.min(...scores) - 10);
  const maxScore = Math.min(100, Math.max(...scores) + 10);
  const range = maxScore - minScore || 1;

  const toX = (i: number) => PAD.left + (i / (sorted.length - 1)) * chartW;
  const toY = (score: number) => PAD.top + chartH - ((score - minScore) / range) * chartH;

  // Construire le path de la courbe (smooth via cubic bezier)
  const pts = sorted.map((s, i) => ({ x: toX(i), y: toY(s.score) }));

  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
  }

  // Path de remplissage (area)
  const areaD = `${d} L ${pts[pts.length - 1].x} ${PAD.top + chartH} L ${pts[0].x} ${PAD.top + chartH} Z`;

  // Labels axe X (3 dates : première, milieu, dernière)
  const xLabels = [0, Math.floor((sorted.length - 1) / 2), sorted.length - 1].map(i => ({
    x: toX(i),
    label: shortDate(formatDate(sorted[i].completedAt)),
  }));

  // Lignes horizontales (0, 50, 100 normalisées)
  const gridLines = [0, 50, 100]
    .filter(v => v >= minScore && v <= maxScore)
    .map(v => ({ y: toY(v), label: String(v) }));

  return (
    <div className="bg-white border border-sand rounded-2xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-lapis" />
          <p className="text-[11px] font-semibold text-lapis uppercase tracking-[1.5px]">Évolution du score</p>
        </div>
        <p className="text-[11px] text-taupe">{sorted.length} sessions</p>
      </div>
      <div className="w-full overflow-hidden">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full"
          style={{ height: 120 }}
          aria-label="Courbe d'évolution du score"
        >
          {/* Lignes de grille horizontales */}
          {gridLines.map(({ y, label }) => (
            <g key={label}>
              <line
                x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                stroke="var(--color-sand)" strokeWidth="0.5" strokeDasharray="3 3"
              />
              <text
                x={PAD.left - 4} y={y + 3.5}
                textAnchor="end"
                fontSize="8"
                fill="var(--color-taupe)"
              >
                {label}
              </text>
            </g>
          ))}

          {/* Aire sous la courbe */}
          <defs>
            <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-lapis)" stopOpacity="0.18" />
              <stop offset="100%" stopColor="var(--color-lapis)" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          <path d={areaD} fill="url(#scoreGrad)" />

          {/* Courbe principale */}
          <path
            d={d}
            fill="none"
            stroke="var(--color-lapis)"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Points */}
          {pts.map((pt, i) => (
            <circle
              key={i}
              cx={pt.x} cy={pt.y} r="2.5"
              fill="var(--color-lapis)"
              stroke="white"
              strokeWidth="1.5"
            />
          ))}

          {/* Labels axe X */}
          {xLabels.map(({ x, label }) => (
            <text
              key={label}
              x={x} y={H - 4}
              textAnchor="middle"
              fontSize="8"
              fill="var(--color-taupe)"
            >
              {label}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
};

// ─── 4. Heatmap8j ─────────────────────────────────────────────────────────────

const Heatmap8j = ({ sessions }: { sessions: SessionWithMeta[] }) => {
  const today = new Date().toISOString().slice(0, 10);
  const sessionDates = new Set(sessions.map(s => formatDate(s.completedAt)));

  // Générer les 8 derniers jours (J-7 à aujourd'hui)
  const days = Array.from({ length: 8 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (7 - i));
    const dateStr = d.toISOString().slice(0, 10);
    const isToday = dateStr === today;
    const done = sessionDates.has(dateStr);
    const dayLabel = d.toLocaleDateString('fr-FR', { weekday: 'short' }).slice(0, 2);
    const dayNum = d.getDate();
    return { dateStr, isToday, done, dayLabel, dayNum };
  });

  const doneCount = days.filter(d => d.done).length;

  return (
    <div className="bg-white border border-sand rounded-2xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-lapis" />
          <p className="text-[11px] font-semibold text-lapis uppercase tracking-[1.5px]">Programme 8 jours</p>
        </div>
        <p className="text-[11px] text-taupe">{doneCount}/8 complétés</p>
      </div>
      <div className="grid grid-cols-8 gap-1.5">
        {days.map(({ dateStr, isToday, done, dayLabel, dayNum }) => (
          <div key={dateStr} className="flex flex-col items-center gap-1">
            <p className="text-[9px] text-taupe uppercase">{dayLabel}</p>
            <div
              className={[
                'w-full aspect-square rounded-lg flex items-center justify-center transition-all',
                done
                  ? 'bg-lapis shadow-sm'
                  : isToday
                  ? 'bg-amber/15 border-2 border-amber'
                  : 'bg-limestone border border-sand/60',
              ].join(' ')}
            >
              {done ? (
                <svg viewBox="0 0 12 12" className="w-3 h-3">
                  <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <span className={`text-[9px] font-medium ${isToday ? 'text-amber' : 'text-taupe'}`}>{dayNum}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Légende */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-sand/60">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-lapis" />
          <span className="text-[10px] text-taupe">Complété</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-amber/15 border border-amber" />
          <span className="text-[10px] text-taupe">Aujourd'hui</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-limestone border border-sand/60" />
          <span className="text-[10px] text-taupe">À venir</span>
        </div>
      </div>
    </div>
  );
};

// ─── 5. ProfileHistory ────────────────────────────────────────────────────────

const ProfileHistory = ({ sessions }: { sessions: SessionWithMeta[] }) => {
  if (sessions.length === 0) return null;

  // Grouper les sessions consécutives avec le même profil
  const timeline: { code: string; count: number; lastDate: string }[] = [];
  for (const s of sessions) {
    const dateStr = formatDate(s.completedAt);
    if (timeline.length > 0 && timeline[0].code === s.profileCode) {
      timeline[0].count++;
    } else {
      timeline.unshift({ code: s.profileCode, count: 1, lastDate: dateStr });
    }
  }
  // Remettre dans l'ordre chronologique (plus récent en haut)
  const display = timeline.slice(0, 6);

  return (
    <div className="bg-white border border-sand rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-4 h-4 text-lapis" />
        <p className="text-[11px] font-semibold text-lapis uppercase tracking-[1.5px]">Historique des profils</p>
      </div>
      <div className="space-y-2">
        {display.map((item, i) => {
          const profileName = PROFILES[item.code]?.name?.split(' / ')[0] ?? item.code;
          const isLatest = i === 0;
          return (
            <div
              key={`${item.code}-${i}`}
              className={[
                'flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-all',
                isLatest
                  ? `${getZoneBg(item.code)} ${getZoneBorder(item.code)}`
                  : 'bg-limestone/50 border-sand/40',
              ].join(' ')}
            >
              {/* Indicateur de position */}
              <div className="flex-shrink-0 flex flex-col items-center gap-0.5">
                <div className={`w-2 h-2 rounded-full ${isLatest ? 'bg-lapis' : 'bg-sand'}`} />
                {i < display.length - 1 && (
                  <div className="w-px h-3 bg-sand/60" />
                )}
              </div>
              {/* Code profil */}
              <div className={`flex-shrink-0 font-mono text-[12px] font-bold min-w-[44px] ${getZoneColor(item.code)}`}>
                {item.code}
              </div>
              {/* Nom */}
              <div className="flex-1 min-w-0">
                <p className="text-[12px] text-ink font-medium leading-tight truncate">{profileName}</p>
                <p className="text-[10px] text-taupe mt-0.5">{shortDate(item.lastDate)}</p>
              </div>
              {/* Nb sessions */}
              {item.count > 1 && (
                <div className="flex-shrink-0 px-2 py-0.5 rounded-full bg-sand/40 text-[10px] text-taupe font-medium">
                  ×{item.count}
                </div>
              )}
              {/* Badge "actuel" */}
              {isLatest && (
                <div className="flex-shrink-0 px-2 py-0.5 rounded-full bg-lapis/10 text-lapis text-[9px] font-semibold uppercase tracking-wide">
                  Actuel
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Écran vide ───────────────────────────────────────────────────────────────

const EmptyState = () => (
  <m.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="flex flex-col items-center text-center px-5 py-12 gap-6"
  >
    {/* Illustration */}
    <div className="w-20 h-20 rounded-2xl bg-lapis/8 flex items-center justify-center">
      <BarChart2 className="w-10 h-10 text-lapis/40" />
    </div>

    {/* Texte */}
    <div className="max-w-[320px]">
      <h2 className="text-[22px] font-serif font-semibold text-ink mb-3 leading-snug">
        Votre tableau de bord vous attend
      </h2>
      <p className="text-[14px] text-taupe leading-relaxed">
        En 90 secondes, faites votre évaluation somatique. Votre score, votre profil polyvagal
        et votre exercice personnalisé apparaissent ici après votre première session.
      </p>
    </div>

    {/* Ce que vous verrez */}
    <div className="w-full max-w-[300px] bg-limestone rounded-xl p-4 text-left space-y-2">
      {[
        { icon: TrendingUp,  label: 'Score de régulation à J+7, J+30' },
        { icon: Calendar,    label: 'Heatmap de vos 8 derniers jours' },
        { icon: BarChart2,   label: 'Courbe de progression' },
        { icon: Clock,       label: 'Historique de vos profils' },
      ].map(({ icon: Icon, label }) => (
        <div key={label} className="flex items-center gap-2.5">
          <Icon className="w-4 h-4 text-lapis/50 flex-shrink-0" />
          <span className="text-[13px] text-ink/70">{label}</span>
        </div>
      ))}
    </div>

    {/* CTA */}
    <AppLink
      to="/fr/evaluation"
      className="inline-flex items-center gap-2 bg-lapis text-white rounded-xl px-7 py-3.5 text-[14px] font-medium hover:bg-lapis-deep transition-all duration-200 hover:-translate-y-px hover:shadow-[0_8px_32px_rgba(43,95,142,0.25)] shadow-sm"
    >
      Faire mon évaluation gratuite
      <ArrowLeft className="w-4 h-4 rotate-180" />
    </AppLink>
    <p className="text-[12px] text-taupe/70">90 secondes · Gratuit · Sans inscription</p>
  </m.div>
);

// ─── Skeleton de chargement ───────────────────────────────────────────────────

const SkeletonCard = ({ h = 80 }: { h?: number }) => (
  <div
    className="bg-white border border-sand rounded-2xl animate-pulse"
    style={{ height: h }}
  />
);

// ─── Composant principal ──────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate();
  const { plan, cohortName, createdAt, planActivatedAt, paymentPriceId } = useAuthContext();

  const handleCheckout = useCallback(async (planId: MolliePlanId) => {
    try { await redirectToCheckout(planId); } catch { /* errors handled in service */ }
  }, []);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aggregates, setAggregates] = useState<AggregateData | null>(null);
  const [sessions, setSessions] = useState<SessionWithMeta[]>([]);
  const [hasUser, setHasUser] = useState(false);
  // syncPending : userId existe mais aucune donnée encore (race condition après quiz)
  const [syncPending, setSyncPending] = useState(false);

  const loadData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);

    const userId = getExistingUserId();
    if (!userId) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    setHasUser(true);

    const [agg, sess] = await Promise.all([
      getAggregates(userId),
      getRecentSessions(userId, 30) as unknown as Promise<SessionWithMeta[]>,
    ]);

    setAggregates(agg);
    setSessions(sess);
    // Si userId existe mais pas encore de données : sync en cours
    setSyncPending(agg === null && sess.length === 0);
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // hasData : userId existe ET au moins aggregates OU sessions disponibles
  const hasData = hasUser && (aggregates !== null || sessions.length > 0);

  return (
    <div className="min-h-screen bg-paper text-ink font-sans">
      <SEO
        title="Mon tableau de bord — Amargi"
        description="Suivi longitudinal de votre système nerveux autonome : score, streak, profils et programme 8 jours."
        path="/fr/tableau-de-bord"
        noindex
      />

      {/* Navigation */}
      <SiteNav logoVariant="primary" />

      {/* Contenu principal */}
      <main className="pt-[80px] pb-20 flex flex-col items-center">
        <div className="w-full max-w-lg px-5">

          {/* En-tête */}
          <div className="pt-8 pb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-[12px] text-taupe hover:text-ink transition-colors mb-6"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Retour
            </button>
            <p className="label-caps text-amber mb-2">Tableau de bord</p>
            <h1 className="text-[28px] font-serif font-light text-ink leading-tight tracking-tight">
              Votre évolution
            </h1>
            <p className="text-[13px] text-taupe mt-1.5 leading-relaxed">
              Chaque session laisse une trace. Voici la vôtre.
            </p>
            {plan === 'pro' && planActivatedAt && (
              <div className="mt-3">
                <ProBadge planActivatedAt={planActivatedAt} planKey={paymentPriceId ?? 'ancrage'} />
              </div>
            )}
            {plan === 'beta' && (
              <div className="mt-3">
                <BetaBadge
                  planActivatedAt={planActivatedAt}
                  cohortName={cohortName}
                  onCheckout={() => handleCheckout('fondatrice')}
                />
              </div>
            )}
          </div>

          {/* États */}
          {loading ? (
            <div className="space-y-4">
              <SkeletonCard h={76} />
              <SkeletonCard h={120} />
              <SkeletonCard h={140} />
              <SkeletonCard h={100} />
              <SkeletonCard h={180} />
            </div>
          ) : !hasUser ? (
            <EmptyState />
          ) : syncPending ? (
            /* Cas race condition : userId existe mais session pas encore syncée */
            <div className="flex flex-col items-center gap-5 py-12 text-center">
              <div className="w-14 h-14 rounded-full bg-lapis/10 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-lapis" />
              </div>
              <div>
                <p className="text-[16px] font-serif font-light text-ink leading-snug">
                  Synchronisation en cours…
                </p>
                <p className="text-[13px] text-taupe mt-1.5 leading-relaxed">
                  Votre session vient d’être enregistrée. Actualisez dans quelques secondes.
                </p>
              </div>
              <button
                onClick={() => loadData(true)}
                disabled={refreshing}
                className="flex items-center gap-2 bg-lapis text-white rounded-xl py-3 px-6 text-[14px] font-medium hover:bg-lapis-deep transition-all disabled:opacity-60"
              >
                {refreshing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Actualiser
              </button>
              <AppLink
                to="/fr/evaluation"
                className="text-[13px] text-taupe underline underline-offset-2 hover:text-ink transition-colors"
              >
                Faire une évaluation
              </AppLink>
            </div>
          ) : !hasData ? (
            <EmptyState />
          ) : (
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="space-y-4"
            >
              {/* Bannière selon le plan */}
              {plan === 'free' && (
                <FreeBanner onCheckout={() => handleCheckout('ancrage')} />
              )}
              {plan === 'trial_8j' && createdAt && (
                <TrialBanner
                  createdAt={createdAt}
                  onCheckout={() => handleCheckout('ancrage')}
                />
              )}

              {/* Widgets — floutés en accès gratuit */}
              <div className={plan === 'free' ? 'blur-sm opacity-40 pointer-events-none select-none' : undefined}>
                <div className="space-y-4">
                  {/* 1. Streak */}
                  {aggregates && aggregates.currentStreak > 0 && (
                    <StreakBadge
                      streak={aggregates.currentStreak}
                      longest={aggregates.longestStreak}
                    />
                  )}

                  {/* 2. Résumé hebdomadaire */}
                  {aggregates && (
                    <WeeklySummary
                      avgScore7d={aggregates.avgScore7d}
                      avgScore30d={aggregates.avgScore30d}
                      dominantProfile={aggregates.dominantProfile}
                      totalSessions={aggregates.totalSessions}
                    />
                  )}

                  {/* 3. Courbe de score */}
                  <ScoreChart sessions={sessions} />

                  {/* 4. Heatmap 8 jours */}
                  <Heatmap8j sessions={sessions} />

                  {/* 5. Historique des profils */}
                  <ProfileHistory sessions={sessions} />
                </div>
              </div>

              {/* CTA — Faire un nouvel exercice */}
              <div className="pt-4 pb-2">
                <AppLink
                  to={`/fr/evaluation`}
                  className="w-full flex items-center justify-center gap-2 bg-lapis text-white rounded-xl py-4 px-5 text-[15px] font-medium hover:bg-lapis-deep transition-all shadow-sm hover:shadow-md"
                >
                  Faire une nouvelle évaluation →
                </AppLink>
                {aggregates?.lastProfileCode && (
                  <AppLink
                    to={`/fr/experimentation/${aggregates.lastProfileCode}`}
                    className="w-full flex items-center justify-center gap-2 mt-3 bg-limestone border border-sand text-ink rounded-xl py-3.5 px-5 text-[14px] font-medium hover:bg-sand/40 transition-all"
                  >
                    Reprendre avec mon profil actuel ({aggregates.lastProfileCode})
                  </AppLink>
                )}
              </div>

              {/* CTA fondatrice (accès à vie) — visible uniquement en beta */}
              {plan === 'beta' && (
                <div className="bg-amber/6 border border-amber/20 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Sparkles className="w-4 h-4 text-amber" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[13px] font-semibold text-ink mb-1">
                        Accès à vie disponible pour les fondatrices
                      </p>
                      <p className="text-[12px] text-taupe leading-relaxed mb-3">
                        Rejoignez Amargi en accès permanent et soutenez le projet dès son lancement.
                      </p>
                      <button
                        onClick={() => handleCheckout('fondatrice')}
                        className="inline-flex items-center gap-1.5 bg-amber text-white rounded-xl px-4 py-2 text-[12px] font-medium hover:opacity-90 transition-all"
                      >
                        Rejoindre à vie · 34,50 €
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Note de confidentialité */}
              <p className="text-[11px] text-taupe/70 text-center leading-relaxed pb-2">
                Vos données sont stockées anonymement sur cet appareil et dans notre base sécurisée.{' '}
                <AppLink to="/fr/confidentialite" className="underline underline-offset-2 hover:text-taupe transition-colors">
                  Politique de confidentialité
                </AppLink>
              </p>
            </m.div>
          )}
        </div>
      </main>

      {/* Footer */}
      <BlogFooter />
    </div>
  );
}
